import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { DreamLogo } from "../../_components/ui-elements/DreamLogo";
import { usePageContext } from "../../_components/context/PageContext";
import { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "@portal/app/_components/ui-elements/Layout";
import { Coach } from "@portal/app/_components/ui-elements/Coach";
import { forwardRef } from "react";

export const SignInContainer = forwardRef(
  (
    {
      onClose,
      showClose,
      children,
    }: React.PropsWithChildren<{
      onClose: () => void;
      showClose?: boolean;
    }>,
    ref
  ) => {
    const { isMobile } = usePageContext();

    return isMobile ? (
      <Container ref={ref} tabIndex={-1}>
        <MobileSloganBox>
          {showClose && (
            <Box position="absolute" color="white">
              <MobileClose onClick={() => onClose()} sx={{ color: "white" }}>
                <FontAwesomeIcon icon={faClose} />
              </MobileClose>
            </Box>
          )}
          <DreamLogoBox>
            <DreamLogo isOnDarkBackground clickable />
          </DreamLogoBox>
        </MobileSloganBox>
        <LoginBox>{children}</LoginBox>
      </Container>
    ) : (
      <Container ref={ref} tabIndex={-1}>
        {showClose && (
          <DesktopClose onClick={() => onClose()}>
            <FontAwesomeIcon icon={faClose} />
          </DesktopClose>
        )}
        <DesktopSloganBox>
          <DreamLogo height={18} isOnDarkBackground />
          <DesktopSloganContent>
            <Slogan variant="new_h2">
              <span>Welcome</span>
              to Dreambound
            </Slogan>
            <SkipperContainer>
              <Coach size={300} persona="friendly" />
            </SkipperContainer>
          </DesktopSloganContent>
        </DesktopSloganBox>
        <LoginBox>{children}</LoginBox>
      </Container>
    );
  }
);

const Container = styled(Box)`
  display: flex;
  --padding: 1.5rem;

  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    height: 100%;
    flex-direction: column;
    background: none;
    border: none;
    border-radius: 0;
    box-shadow: 0 0 3rem rgba(0, 0, 0, 0.1);
    background: white;
  }

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    justify-content: space-between;
    width: 100%;
    max-width: 900px;
    min-height: 516px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 1rem;
    background: ${({ theme }) => theme.palette.primary.main};
    overflow: hidden;
  }
`;

const MobileSloganBox = styled(Box)`
  display: flex;
  height: var(--NavBarHeight);
  align-items: center;
  background: ${({ theme }) => theme.palette.primary.main};
`;

const DreamLogoBox = styled(Box)`
  flex: 1;
  justify-content: center;
  display: flex;
`;

const DesktopSloganBox = styled(Box)`
  display: flex;
  width: 441px;
  padding: var(--padding);
  flex-direction: column;
`;

const DesktopSloganContent = styled(Box)`
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DesktopClose = styled(IconButton)`
  position: absolute;
  top: calc(var(--padding) - 0.5rem);
  right: calc(var(--padding) - 0.5rem);
`;

const MobileClose = styled(IconButton)`
  padding: 0.8rem 1.4rem;
`;

const Slogan = styled(Typography)`
  display: flex;
  flex-direction: column;
  padding-top: var(--padding);
  margin: 0;
  font-size: 2rem;
  color: white;
`;

const LoginBox = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: var(--padding);
  flex: 1;

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    min-width: 420px;
    max-width: calc(400px + 2 * var(--padding));
    background: white;
    border-radius: calc(1rem - 1px);
  }
`;

const SkipperContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
